import React, { useState, useEffect, useMemo } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { isValidCurrencyForTransactionProcess } from '../../../../util/fieldHelpers';
import {
  maxLength,
  required,
  composeValidators,
  moneySubUnitAmountAtLeast,
  autocompleteSearchRequired,
  autocompletePlaceSelected,
} from '../../../../util/validators';

// Import shared components
import {
  Form,
  FieldSelect,
  FieldTextInput,
  FieldLocationAutocompleteInput,
  Heading,
  Accordion,
  FieldCurrencyInput,
  CustomExtendedDataField,
} from '../../../../components';
import appSettings from '../../../../config/settings';
import useInterfaceLang from '../../../../hooks/useInterfaceLang';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import { formatMoney } from '../../../../util/currency';
import {
  LISTING_CONDITION,
  LISTING_SALE_CLASS,
  LISTING_CATEGORY,
} from '../../../../constants/listing.constants';
import { PRICE_TYPE } from '../../../../constants/global.constants';

// Import modules from this directory
import FormButtons from '../FormButtons/FormButtons';
import { GeneralListingData } from './EditListingDetailsFields';
import useItems from './EditListingDetails.hooks';

import css from './EditListingDetailsForm.module.css';

const TITLE_MAX_LENGTH = 60;

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingDetailsForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingDetailsForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingDetailsForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

// Field component that either allows selecting listing type (if multiple types are available)
// or just renders hidden fields:
// - listingType              Set of predefined configurations for each listing type
// - transactionProcessAlias  Initiate correct transaction against Marketplace API
// - unitType                 Main use case: pricing unit
const FieldSelectListingType = props => {
  const {
    name,
    listingTypes,
    hasExistingListingType,
    onListingTypeChange,
    formApi,
    formId,
    intl,
  } = props;
  const hasMultipleListingTypes = listingTypes?.length > 1;

  const handleOnChange = value => {
    const selectedListingType = listingTypes.find(config => config.listingType === value);
    formApi.change('transactionProcessAlias', selectedListingType.transactionProcessAlias);
    formApi.change('unitType', selectedListingType.unitType);

    if (onListingTypeChange) {
      onListingTypeChange(selectedListingType);
    }
  };
  const getListingTypeLabel = listingType => {
    const listingTypeConfig = listingTypes.find(config => config.listingType === listingType);
    return listingTypeConfig ? listingTypeConfig.label : listingType;
  };

  return hasMultipleListingTypes && !hasExistingListingType ? (
    <>
      <FieldSelect
        id={formId ? `${formId}.${name}` : name}
        name={name}
        className={css.listingTypeSelect}
        label={intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
        validate={required(
          intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeRequired' })
        )}
        onChange={handleOnChange}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypePlaceholder' })}
        </option>
        {listingTypes.map(config => {
          const type = config.listingType;
          return (
            <option key={type} value={type}>
              {config.label}
            </option>
          );
        })}
      </FieldSelect>
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  ) : hasMultipleListingTypes && hasExistingListingType ? (
    <div className={css.listingTypeSelect}>
      <Heading as="h5" rootClassName={css.selectedLabel}>
        {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
      </Heading>
      <p className={css.selectedValue}>{getListingTypeLabel(formApi.getFieldState(name)?.value)}</p>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </div>
  ) : (
    <>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  );
};

// Finds the correct subcategory within the given categories array based on the provided categoryIdToFind.
const findCategoryConfig = (categories, categoryIdToFind) => {
  return categories?.find(category => category.id === categoryIdToFind);
};

/**
 * Recursively render subcategory field inputs if there are subcategories available.
 * This function calls itself with updated props to render nested category fields.
 * The select field is used for choosing a category or subcategory.
 */
const CategoryField = props => {
  const { currentCategoryOptions, level, values, prefix, handleCategoryChange, intl } = props;

  const currentCategoryKey = `${prefix}${level}`;

  const categoryConfig = findCategoryConfig(currentCategoryOptions, values[`${prefix}${level}`]);

  return (
    <>
      {currentCategoryOptions ? (
        <FieldSelect
          key={currentCategoryKey}
          id={currentCategoryKey}
          name={currentCategoryKey}
          className={css.listingTypeSelect}
          onChange={event => handleCategoryChange(event, level, currentCategoryOptions)}
          label={intl.formatMessage(
            { id: 'EditListingDetailsForm.categoryLabel' },
            { categoryLevel: currentCategoryKey }
          )}
          validate={required(
            intl.formatMessage(
              { id: 'EditListingDetailsForm.categoryRequired' },
              { categoryLevel: currentCategoryKey }
            )
          )}
        >
          <option disabled value="">
            {intl.formatMessage(
              { id: 'EditListingDetailsForm.categoryPlaceholder' },
              { categoryLevel: currentCategoryKey }
            )}
          </option>

          {currentCategoryOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </FieldSelect>
      ) : null}

      {categoryConfig?.subcategories?.length > 0 ? (
        <CategoryField
          currentCategoryOptions={categoryConfig.subcategories}
          level={level + 1}
          values={values}
          prefix={prefix}
          handleCategoryChange={handleCategoryChange}
          intl={intl}
        />
      ) : null}
    </>
  );
};

const FieldSelectCategory = props => {
  useEffect(() => {
    checkIfInitialValuesExist();
  }, []);

  const { prefix, listingCategories, formApi, intl, setAllCategoriesChosen, values } = props;

  // Counts the number of selected categories in the form values based on the given prefix.
  const countSelectedCategories = () => {
    return Object.keys(values).filter(key => key.startsWith(prefix)).length;
  };

  // Checks if initial values exist for categories and sets the state accordingly.
  // If initial values exist, it sets `allCategoriesChosen` state to true; otherwise, it sets it to false
  const checkIfInitialValuesExist = () => {
    const count = countSelectedCategories(values, prefix);
    setAllCategoriesChosen(count > 0);
  };

  // If a parent category changes, clear all child category values
  const handleCategoryChange = (category, level, currentCategoryOptions) => {
    const selectedCatLenght = countSelectedCategories();
    if (level < selectedCatLenght) {
      for (let i = selectedCatLenght; i > level; i--) {
        formApi.change(`${prefix}${i}`, null);
      }
    }
    const categoryConfig = findCategoryConfig(currentCategoryOptions, category).subcategories;
    setAllCategoriesChosen(!categoryConfig || categoryConfig.length === 0);
  };

  return (
    <CategoryField
      currentCategoryOptions={listingCategories}
      level={1}
      values={values}
      prefix={prefix}
      handleCategoryChange={handleCategoryChange}
      intl={intl}
    />
  );
};

/**
 * Form that asks title, description, transaction process and unit type for pricing
 * In addition, it asks about custom fields according to marketplace-custom-config.js
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.formId] - The form id
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {boolean} props.updated - Whether the form is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Object} props.fetchErrors - The fetch errors object
 * @param {propTypes.error} [props.fetchErrors.createListingDraftError] - The create listing draft error
 * @param {propTypes.error} [props.fetchErrors.showListingsError] - The show listings error
 * @param {propTypes.error} [props.fetchErrors.updateListingError] - The update listing error
 * @param {Function} props.pickSelectedCategories - The pick selected categories function
 * @param {Array<Object>} props.selectableListingTypes - The selectable listing types
 * @param {boolean} props.hasExistingListingType - Whether the listing type is existing
 * @param {propTypes.listingFields} props.listingFieldsConfig - The listing fields config
 * @param {string} props.listingCurrency - The listing currency
 * @param {string} props.saveActionMsg - The save action message
 * @param {boolean} [props.autoFocus] - Whether the form should autofocus
 * @param {Function} props.onListingTypeChange - The listing type change function
 * @param {Function} props.onSubmit - The submit function
 * @returns {JSX.Element}
 */
const EditListingDetailsForm = props => {
  const { formId = 'EditListingDetailsForm', listingFieldsConfig = [] } = props;
  const { language } = useInterfaceLang();

  const configsByKey = useMemo(
    () => listingFieldsConfig?.reduce((configs, conf) => ({ ...configs, [conf.key]: conf }), {}),
    [listingFieldsConfig]
  );

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          autoFocus,
          className,
          disabled,
          ready,
          form: formApi,
          handleSubmit,
          onListingTypeChange,
          invalid,
          pristine,
          marketplaceCurrency,
          marketplaceName,
          selectableListingTypes,
          selectableCategories,
          hasExistingListingType = false,
          categoryPrefix,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
          listingCurrency,
          values,
          onGoBackATab,
          draftingInProgress,
          listingMinimumPriceSubUnits = 0,
          unitType,
          isDraft,
        } = formRenderProps;

        const intl = useIntl();
        const { listingType, transactionProcessAlias } = values;
        const [allCategoriesChosen, setAllCategoriesChosen] = useState(false);
        const { boatItems, engineItems, smallBoatItems, trailerItems } = useItems({
          formId,
          configsByKey,
          formApi,
        });

        const titleRequiredMessage = intl.formatMessage({
          id: 'EditListingDetailsForm.titleRequired',
        });
        const maxLengthMessage = intl.formatMessage(
          { id: 'EditListingDetailsForm.maxLength' },
          {
            maxLength: TITLE_MAX_LENGTH,
          }
        );

        // Determine the currency to validate:
        // - If editing an existing listing, use the listing's currency.
        // - If creating a new listing, fall back to the default marketplace currency.
        const currencyToCheck = listingCurrency || marketplaceCurrency;

        // Verify if the selected listing type's transaction process supports the chosen currency.
        // This checks compatibility between the transaction process
        // and the marketplace or listing currency.
        const isCompatibleCurrency = isValidCurrencyForTransactionProcess(
          transactionProcessAlias,
          currencyToCheck
        );

        const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);

        const { Money } = sdkTypes;
        const intl_ref = intl;
        const getPriceValidators = () => {
          const priceRequiredMsg = intl_ref.formatMessage({
            id: 'EditListingPricingForm.priceRequired',
          });
          const priceRequired = required(priceRequiredMsg);
          if (!listingMinimumPriceSubUnits) return priceRequired;
          const minPriceRaw = new Money(listingMinimumPriceSubUnits, marketplaceCurrency);
          const minPrice = formatMoney(intl_ref, minPriceRaw);
          const priceTooLowMsg = intl_ref.formatMessage(
            { id: 'EditListingPricingForm.priceTooLow' },
            { minPrice }
          );
          return composeValidators(
            priceRequired,
            moneySubUnitAmountAtLeast(priceTooLowMsg, listingMinimumPriceSubUnits)
          );
        };
        const priceValidators = getPriceValidators();

        const hasCategories = selectableCategories && selectableCategories.length > 0;
        const showCategories = listingType && hasCategories;

        const showTitle = false;
        const showDescription = true;

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const hasMandatoryListingTypeData =
          listingType && transactionProcessAlias && (unitType || values.unitType);
        const submitDisabled =
          invalid ||
          disabled ||
          submitInProgress ||
          !hasMandatoryListingTypeData ||
          !isCompatibleCurrency;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <ErrorMessage fetchErrors={fetchErrors} />

            <FieldSelectListingType
              name="listingType"
              listingTypes={selectableListingTypes}
              hasExistingListingType={hasExistingListingType}
              onListingTypeChange={onListingTypeChange}
              formApi={formApi}
              formId={formId}
              intl={intl}
            />

            {showCategories && isCompatibleCurrency && (
              <FieldSelectCategory
                values={values}
                prefix={categoryPrefix}
                listingCategories={selectableCategories}
                formApi={formApi}
                intl={intl}
                allCategoriesChosen={allCategoriesChosen}
                setAllCategoriesChosen={setAllCategoriesChosen}
              />
            )}

            {showTitle && isCompatibleCurrency && (
              <FieldTextInput
                id={`${formId}title`}
                name="title"
                className={css.title}
                type="text"
                label={intl.formatMessage({ id: 'EditListingDetailsForm.title' })}
                placeholder={intl.formatMessage({
                  id: 'EditListingDetailsForm.titlePlaceholder',
                })}
                maxLength={TITLE_MAX_LENGTH}
                validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
                required
                autoFocus={autoFocus}
              />
            )}

            <GeneralListingData
              formId={formId}
              intl={intl}
              categoryValue={values['pub_category']}
              typeValue={values['pub_type']}
              configsByKey={configsByKey}
              isDraft={isDraft}
            />

            {isCompatibleCurrency && (
              <div className={css.pricingSection}>
                {/* Row 1: Staat + Bouwjaar */}
                <div className={css.pricingRow}>
                  <FieldSelect
                    name="saleClass"
                    id={`${formId}saleClass`}
                    className={css.pricingField}
                    label={intl.formatMessage({ id: 'EditListingLocationForm.saleClassLabel' })}
                    required
                    validate={required(intl.formatMessage({ id: 'Global.required' }))}
                  >
                    <option disabled value="">
                      <FormattedMessage id="EditListingLocationForm.conditionDefaultOption" />
                    </option>
                    {Object.values(LISTING_SALE_CLASS).map(sc => (
                      <option key={sc} value={sc}>
                        <FormattedMessage id={`General.listing.saleClass.${sc}`} />
                      </option>
                    ))}
                  </FieldSelect>

                  {![LISTING_CATEGORY.EQUIPMENT, LISTING_CATEGORY.MOORING].includes(
                    values['pub_category']
                  ) && (
                    <CustomExtendedDataField
                      name="pub_yearBuilt"
                      key="pub_yearBuilt"
                      fieldConfig={configsByKey?.yearBuilt}
                      formId={formId}
                      className={classNames(css.pricingField, css.noMarginBottom)}
                    />
                  )}
                </div>

                {/* Row 2: Condition (only when used) */}
                {values.saleClass === LISTING_SALE_CLASS.USED && (
                  <div className={css.pricingRow}>
                    <FieldSelect
                      name="condition"
                      id={`${formId}condition`}
                      className={css.pricingField}
                      label={intl.formatMessage({ id: 'EditListingLocationForm.conditionLabel' })}
                    >
                      <option disabled value="">
                        <FormattedMessage id="EditListingLocationForm.conditionDefaultOption" />
                      </option>
                      <option value=""></option>
                      {Object.values(LISTING_CONDITION).map(c => (
                        <option key={c} value={c}>
                          <FormattedMessage id={`General.listing.condition.${c}`} />
                        </option>
                      ))}
                    </FieldSelect>
                  </div>
                )}

                {/* Row 3: Prijs + Prijstype */}
                <div className={css.pricingRow}>
                  <FieldCurrencyInput
                    id={`${formId}price`}
                    name="price"
                    autocomplete="off"
                    className={css.pricingField}
                    label={`${intl.formatMessage(
                      { id: 'EditListingPricingForm.pricePerProduct' },
                      { unitType }
                    )}*`}
                    placeholder={intl.formatMessage({
                      id: 'EditListingPricingForm.priceInputPlaceholder',
                    })}
                    currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                    validate={priceValidators}
                  />

                  <FieldSelect
                    name="priceType"
                    id={`${formId}priceType`}
                    className={css.pricingField}
                    label={`${intl.formatMessage({
                      id: 'EditListingLocationForm.priceTypeLabel',
                    })}*`}
                    validate={required(
                      intl.formatMessage({ id: 'EditListingLocationForm.priceTypeRequired' })
                    )}
                    required
                  >
                    <option disabled value="">
                      <FormattedMessage id="EditListingLocationForm.priceTypeDefaultOption" />
                    </option>
                    {Object.values(PRICE_TYPE).map(pt => (
                      <option key={pt} value={pt}>
                        {intl.formatMessage({ id: `General.price.priceType.${pt}` })}
                      </option>
                    ))}
                  </FieldSelect>
                </div>

                {/* Row 4: BTW inbegrepen */}
                <div className={css.pricingRow}>
                  <FieldSelect
                    name="vatIncluded"
                    id={`${formId}vatIncluded`}
                    className={css.pricingField}
                    label={intl.formatMessage({ id: 'EditListingLocationForm.vatIncludedLabel' })}
                    validate={required(
                      intl.formatMessage({ id: 'EditListingLocationForm.vatIncludedRequired' })
                    )}
                    required
                  >
                    <option disabled value="">
                      <FormattedMessage id="EditListingLocationForm.vatIncludedDefaultOption" />
                    </option>
                    <option key="yes" value={1}>
                      <FormattedMessage id="Global.yes" />
                    </option>
                    <option key="no" value={0}>
                      <FormattedMessage id="Global.no" />
                    </option>
                    <option key="not-applied" value={2}>
                      <FormattedMessage id="EditListingLocationForm.vatNotApplicable" />
                    </option>
                  </FieldSelect>
                </div>
              </div>
            )}

            {isCompatibleCurrency && (
              <FieldLocationAutocompleteInput
                rootClassName={css.locationAddress}
                inputClassName={css.locationAutocompleteInput}
                iconClassName={css.locationAutocompleteInputIcon}
                predictionsClassName={css.predictionsRoot}
                name="location"
                label={`${intl.formatMessage({ id: 'EditListingLocationForm.address' })}*`}
                placeholder={intl.formatMessage({
                  id: 'EditListingLocationForm.addressPlaceholder',
                })}
                useDefaultPredictions={false}
                format={v => v}
                valueFromForm={values.location}
                types={['place']}
                language={language}
                validate={composeValidators(
                  autocompleteSearchRequired(
                    intl.formatMessage({ id: 'EditListingLocationForm.addressRequired' })
                  ),
                  autocompletePlaceSelected(
                    intl.formatMessage({ id: 'EditListingLocationForm.addressNotRecognized' })
                  )
                )}
              />
            )}

            {showDescription && isCompatibleCurrency && (
              <FieldTextInput
                id={`${formId}description`}
                name="description"
                className={css.description}
                type="textarea"
                label={intl.formatMessage({ id: 'EditListingDetailsForm.description' })}
                placeholder={intl.formatMessage({
                  id: 'EditListingDetailsForm.descriptionPlaceholder',
                })}
              />
            )}

            <Accordion
              className={css.accordion}
              items={
                [LISTING_CATEGORY.POWER_BOATS, LISTING_CATEGORY.SAILING_BOATS].includes(
                  values['pub_category']
                )
                  ? boatItems
                  : values['pub_category'] === LISTING_CATEGORY.BOAT_ENGINES
                  ? engineItems
                  : values['pub_category'] === LISTING_CATEGORY.TRAILERS
                  ? trailerItems
                  : values['pub_category'] === LISTING_CATEGORY.EQUIPMENT ||
                    values['pub_category'] === LISTING_CATEGORY.MOORING
                  ? []
                  : smallBoatItems
              }
            />

            {!isCompatibleCurrency && listingType && (
              <p className={css.error}>
                <FormattedMessage
                  id="EditListingDetailsForm.incompatibleCurrency"
                  values={{ marketplaceName, marketplaceCurrency }}
                />
              </p>
            )}
            <FormButtons
              className={css.buttons}
              saveActionMsg={saveActionMsg}
              submitDisabled={submitDisabled}
              submitInProgress={submitInProgress}
              submitReady={submitReady}
              formApi={formApi}
              onGoBackATab={onGoBackATab}
              draftingInProgress={draftingInProgress}
            />
          </Form>
        );
      }}
    />
  );
};

export default EditListingDetailsForm;
